import React, { useEffect, useRef, useState } from "react";

import Dragger from "../../Utils/Dragger/Dragger";
import { appstr } from "@coreapp/utils/appstr";
import { GetCurrentBoxSelection, GetDoc } from "@/Utils/StateLib";
import { EmployeeBaseballCard, EmployeeCardProps } from "./EmployeeBaseballCard";
import { isNullish, Overlay } from "@theorgchartdev/storybook";
import { PositionBaseballCard, PositionCardProps } from "./PositionBaseballCard";
import type { QObjDBObject } from "@coreapp/CoreApp/QObjDBObject";
import { useTopic } from "@/hooks/useTopic";
import { useNavigationContext } from "@/providers/NavigationProvider";
import { ErrorBoundary } from "../../Utils/ErrorBoundary/ErrorBoundary";
import FocusTrap from "focus-trap-react";
import {
  CardWrapper,
  cardFocusTrapOptions
} from "@/appComponents/Blocks/Cards/CardWrapper/CardWrapper";
import { getContainerHeight, getContainerWidth } from "@/Utils/getContainerSize";
import { BoxChip } from "@/Utils/BoxChip";
import { useFavorites } from "@/hooks/useFavorites";
import { filterInternalFields } from "@/Utils/FilterInternalFields";
import { QXDoc } from "@coreapp/CoreApp/QPlanManager";
import { ProfileTypes } from "./ProfileBaseballCard.utils";
import { enumObjClassNames, kPositionCountFields, kEmployeeCountFields } from "@coreapp/constants";
import { AgentBaseballCard } from "./Agent/AgentBaseballCard";
import {
  getAgentSettingsFromHeadingConfig,
  getAvailableAgentSlots,
  isAgentProfileEnabled
} from "./Agent/AgentProfile.constants";

const getVisibleFields = (doc: QXDoc, type: ProfileTypes, unusedFavoriteFields?: string[]) => {
  const viewMgr = doc.GetViewMgr();

  if (!viewMgr) {
    return null;
  }

  let allVisibleFields = viewMgr.GetFieldList(
    type === ProfileTypes.Employee ? enumObjClassNames.box : enumObjClassNames.position
  );

  if (type === ProfileTypes.Employee) {
    // Default favorites are excluded by default from the visible fields. If the user unstars any of them,
    // we need to add them to the fields.
    unusedFavoriteFields?.forEach(field => {
      const index = allVisibleFields.indexOf(field);
      if (index === -1) {
        allVisibleFields.push(field);
      }
    });
  }

  let onlyVisibleFields = filterInternalFields(allVisibleFields);

  // Filter out employee count fields from position profile cards
  if (type === ProfileTypes.Position) {
    onlyVisibleFields = onlyVisibleFields.filter(
      (field: string) => !kEmployeeCountFields.includes(field as any)
    );
  }

  // Filter out position count fields from employee profile cards
  if (type === ProfileTypes.Employee) {
    onlyVisibleFields = onlyVisibleFields.filter(
      (field: string) => !kPositionCountFields.includes(field as any)
    );
  }

  return onlyVisibleFields;
};

type Props = {
  show: boolean;
  target: QObjDBObject | null | undefined;
  parentReference: QObjDBObject | null;
  ignoreSelection?: boolean;
  onClose: () => void;
};

const ProfileBaseballCardInner = ({ show, target, ignoreSelection, onClose }: Props) => {
  const { LaunchUIElement, isPhoneLayout } = useNavigationContext();

  const [showMoreFields, setShowMoreFields] = useState<boolean>(false);
  const favoriteController = useFavorites(GetDoc()?.GetChartID());

  const stateRef = useRef({ show, target, ignoreSelection });
  stateRef.current = { show, target, ignoreSelection };
  const isPositionChart = GetDoc()?.IsPositionDocument() ?? false;

  // When switching subcharts, clear out the specified target
  // This will make the top box of the new subchart the subject
  useTopic("subchartSwitch", () => {
    if (!stateRef.current.show || isNullish(stateRef.current.target)) {
      return;
    }

    LaunchUIElement("profile", true, {});
  });

  // Reset ignoreSelection when canvas selection changes
  useTopic("CanvasSelectionChange", () => {
    if (stateRef.current.ignoreSelection) {
      LaunchUIElement("profile", true, {
        target: stateRef.current.target
      });
    }
  });

  const doc = GetDoc();
  let dataObj: Nullish<QObjDBObject> = null;

  if (target) {
    dataObj = target;
  } else {
    dataObj = doc?.GetSubChartMgr()?.GetCurrentSubChart()?.GetTopDBObj();
  }

  if (!ignoreSelection) {
    const selectedBox = GetCurrentBoxSelection();
    if (selectedBox) {
      dataObj = selectedBox.GetDataObj();
    }
  }

  const record = dataObj ? dataObj.JSON() : {};

  // INS-7: agent records get their own card, gated to the isolved brand — the
  // gate wraps the object check so isAgent data never routes here off-brand.
  const isAgentCard = isAgentProfileEnabled() && !!dataObj?.IsAgentObj();

  useEffect(() => {
    const isPosition = dataObj ? dataObj?.IsPositionObj() : doc?.IsPositionDocument();
    // Positions and agents start collapsed; employees start expanded.
    setShowMoreFields(!(isPosition || isAgentCard));
  }, [dataObj?.GetObjectRecordID()]);

  if (!doc || !show) {
    return null;
  }

  const cardProps = () => {
    const commonProps: any = {
      doc,
      record,
      showMoreFields,
      setShowMoreFields,
      isPositionChart,
      dataObj,
      headingConfig: doc.GetProfileMgr()?.fHeadingConfig,
      mapping: doc?.GetIWizData()?.fMapping ?? {}
    };

    const employeeCardSpecificProps = {
      visibleFields:
        getVisibleFields(doc, ProfileTypes.Employee, favoriteController.unusedDefaultFavorites) ||
        [],
      favoriteController
    };

    if (dataObj?.IsPositionObj()) {
      return {
        ...commonProps,
        nAssignees: dataObj?.NumHolders(),
        reportsToLabel: dataObj?.GetParent()
          ? BoxChip.FromPositionObjDBObject(dataObj?.GetParent()).label()
          : undefined,
        visibleFields: getVisibleFields(doc, ProfileTypes.Position) || [],
        employeeCardProps: employeeCardSpecificProps
      } as PositionCardProps;
    } else {
      return {
        ...commonProps,
        ...employeeCardSpecificProps
      } as EmployeeCardProps;
    }
  };

  const agentSettings = getAgentSettingsFromHeadingConfig(doc.GetProfileMgr()?.fHeadingConfig);
  const cardContent = isAgentCard ? (
    <AgentBaseballCard
      record={record}
      agentConfig={agentSettings.fields}
      includedSlotIds={agentSettings.shownSlots ?? null}
      // Slots hide when their mapped column wasn't pushed (no field def).
      availableSlotIds={getAvailableAgentSlots(
        fieldName => !!dataObj?.GetFieldDefByID(fieldName),
        agentSettings.fields
      )}
      showMoreFields={showMoreFields}
      setShowMoreFields={setShowMoreFields}
    />
  ) : doc.IsPositionDocument() && dataObj?.IsPositionObj() ? (
    <PositionBaseballCard {...(cardProps() as PositionCardProps)} />
  ) : (
    <EmployeeBaseballCard includeScrollableArea {...(cardProps() as EmployeeCardProps)} />
  );

  if (isPhoneLayout) {
    const size = { width: getContainerWidth(), height: getContainerHeight() };

    return (
      <ErrorBoundary onError={onClose}>
        <CardWrapper
          show={true}
          prefixId="profile-card"
          contentClass="ocn-profile-card"
          onClose={onClose}
          focusTrap
          otherHeaderProps={{ title: appstr(isAgentCard ? "Agent profile" : "Profile") }}
          otherFooterProps={undefined}
          otherOverlayProps={undefined}
          customClass={undefined}
          size={size}
          onMouseDownResize={undefined}
          initOffset={{ x: 0, y: 0 }}
          minWidth={size.width}
          minHeight={size.height}
        >
          {cardContent}
        </CardWrapper>
      </ErrorBoundary>
    );
  }

  // Expanded agent cards grow to the full container height — the six "Show
  // more" sections are designed to use it. Collapsed (and every non-agent
  // card) keeps the fixed 600px window.
  const useFullCardHeight = isAgentCard && !!showMoreFields;

  return (
    <ErrorBoundary onError={onClose}>
      <Dragger>
        {(ref: React.MutableRefObject<HTMLDivElement>) => (
          <FocusTrap focusTrapOptions={cardFocusTrapOptions}>
            <Overlay
              id={"profile-card"}
              ref={ref}
              show={true}
              onClose={onClose}
              headerProps={{
                title: appstr(isAgentCard ? "Agent profile" : "Profile"),
                headerActions: {
                  closeAction: onClose
                },
                modifier: "dark"
              }}
              className={`oc-profile-card-overlay${
                useFullCardHeight ? " oc-profile-card-overlay--expanded" : ""
              }`}
              padding="16px"
              sizeProps={
                useFullCardHeight
                  ? // Heights come from the --expanded modifier (definite 90cqh
                    // overlay + flex-filled body). sizeProps heights land on the
                    // overlay BODY, whose ancestors are auto-height, so inline
                    // percentages collapse to content height — an offscreen card
                    // with no scrollbar.
                    { minWidth: "400px", maxWidth: "400px" }
                  : {
                      minWidth: "400px",
                      maxWidth: "400px",
                      minHeight: "600px",
                      maxHeight: "75%",
                      height: "1px" // Necessary to make the height of child elements not collapse. Should not cause weird heights due to min/max height settings.
                    }
              }
            >
              <div className="ocn-profile-card" style={{ height: "100%" }}>
                {cardContent}
              </div>
            </Overlay>
          </FocusTrap>
        )}
      </Dragger>
    </ErrorBoundary>
  );
};

export const ProfileBaseballCard = ProfileBaseballCardInner;
